import {ChangeDetectionStrategy, Component, computed, inject, output, signal} from '@angular/core';
import * as _ from 'lodash';
import {bzIconChevronDown, bzIconChevronRight} from '@bringoz/ui/icons';
import {PANEL_DATA} from '../../../../commons/angular/services/panel.service';
import {TimekeepingViewSettings} from '../../models/timekeeping-list.model';

export interface TimekeepingColumnsSettingsPanelData {
  settings: TimekeepingViewSettings;
}

export enum TimekeepingColumnsSettingsAction {
  Submit = 'Submit',
  Reset = 'Reset',
  Cancel = 'Cancel'
}

export interface TimekeepingColumnsSettingsResult {
  action: TimekeepingColumnsSettingsAction;
  settings?: TimekeepingViewSettings;
}

interface TimekeepingColumnRow {
  key: string;
  labelKey: string;
  enabled: boolean;
  permission?: string;
}

const COLUMNS_TRANSLATION_PREFIX = 'TIMEKEEPING_TABLE_COLUMNS';

require('./timekeeping-columns-settings-panel.scss');
@Component({
  standalone: false,
  selector: 'app-timekeeping-columns-settings-panel',
  templateUrl: './timekeeping-columns-settings-panel.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimekeepingColumnsSettingsPanelComponent {

  // PanelService closes the panel on this output, so the name is part of its contract.
  readonly onClose = output<TimekeepingColumnsSettingsResult>();

  private readonly data = inject<TimekeepingColumnsSettingsPanelData>(PANEL_DATA);
  private readonly settingsDraft = signal<TimekeepingViewSettings>(_.cloneDeep(this.data?.settings));

  readonly isColumnsOpen = signal(true);
  readonly icons = {expanded: bzIconChevronDown, collapsed: bzIconChevronRight};
  readonly rows = computed<TimekeepingColumnRow[]>(() =>
    _.map(_.toPairs(this.settingsDraft()?.columns), ([key, column]) => ({
      key,
      labelKey: `${COLUMNS_TRANSLATION_PREFIX}.${key}`,
      enabled: column.enabled,
      permission: column.permission
    }))
  );

  onToggleColumnsOpen() {
    this.isColumnsOpen.update((isOpen) => !isOpen);
  }

  onToggleColumn(row: TimekeepingColumnRow, enabled: boolean) {
    this.settingsDraft.update((settings) => ({
      ...settings,
      columns: {...settings.columns, [row.key]: {...settings.columns[row.key], enabled}}
    }));
  }

  onSubmit() {
    this.onClose.emit({action: TimekeepingColumnsSettingsAction.Submit, settings: this.settingsDraft()});
  }

  onReset() {
    this.onClose.emit({action: TimekeepingColumnsSettingsAction.Reset});
  }

  onCancel() {
    this.onClose.emit({action: TimekeepingColumnsSettingsAction.Cancel});
  }
}
